import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  Check, X, ArrowRight, Sparkle, Storefront, Handshake, Buildings,
  Confetti, Leaf,
} from "@phosphor-icons/react";
import FarmersNav from "@/components/FarmersNav";
import { useAuth } from "@/contexts/AuthContext";

/**
 * FarmersBilling — Farmers Pulse pricing / plans page.
 *
 *  - Anonymous visitors see 4-column pricing (Free · Grower · Business · Cooperative).
 *  - Signed-in farmers also see their current plan + usage snapshot at the top,
 *    with an inline Upgrade / Manage billing CTA.
 *
 *  Free is the push-cart-friendly default (10 products max).
 *  Cooperative captures a sales lead — no Stripe checkout.
 */

const PLAN_ICON = {
  free: Leaf, grower: Storefront, business: Confetti, cooperative: Buildings,
};

export default function FarmersBilling() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [mine, setMine] = useState(null);
  const [interval, setInterval] = useState("month");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [coopOpen, setCoopOpen] = useState(false);

  useEffect(() => {
    http.get("/billing/plans").then((r) => setPlans(r.data.plans));
  }, []);
  useEffect(() => {
    if (!user) { setLoading(false); return; }
    http.get("/billing/me")
      .then((r) => setMine(r.data))
      .catch(() => { /* not authed, ignore */ })
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (params.get("canceled")) toast.info("Checkout canceled — you can restart anytime.");
  }, [params]);

  const upgrade = async (plan) => {
    if (!user) { toast.error("Sign in first"); return; }
    if (plan.id === "cooperative") { setCoopOpen(true); return; }
    if (plan.id === "free") { toast.info("You're already on the Free plan (or downgrade via Manage billing)"); return; }
    const lookup = plan.lookup_keys[interval];
    if (!lookup) { toast.error("Plan not available"); return; }
    setBusy(plan.id);
    try {
      const r = await http.post("/billing/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.assign(r.data.checkout_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
    } finally { setBusy(null); }
  };

  const openPortal = async () => {
    try {
      const r = await http.post("/billing/portal", { return_url: `${window.location.origin}/farmers/billing` });
      window.location.assign(r.data.portal_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal");
    }
  };

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="farmers-billing-page">
      <div className="max-w-6xl mx-auto"><FarmersNav /></div>

      <div className="max-w-6xl mx-auto mt-6">
        <header>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <Sparkle size={12} weight="fill" className="inline mr-1" /> Plans & Pricing
          </div>
          <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
            Grow at your pace — free for push-cart farmers, paid when you're ready.
          </h1>
          <p className="mt-2 max-w-3xl text-[#3A3830]">
            Farmers Pulse never charges commissions on your sales. Pick a monthly plan
            that fits your farm — cancel or downgrade any time.
          </p>
        </header>

        {/* Current plan banner (signed-in) */}
        {user && mine && (
          <section className="mt-6 rounded-3xl border border-black/10 bg-white p-5" data-testid="farmers-billing-current">
            <div className="flex flex-wrap items-center gap-3">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Your plan</div>
              <div className="font-serif-display text-2xl">{mine.plan.name}</div>
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase">
                Status {mine.subscription.status}
              </span>
              <div className="ml-auto flex flex-wrap gap-2">
                {mine.subscription.stripe_customer_id && (
                  <button onClick={openPortal}
                          data-testid="farmers-billing-portal-btn"
                          className="rounded-full border border-black/15 bg-white px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[color:var(--np-brand,#B85F2A)] hover:text-[color:var(--np-brand,#B85F2A)]">
                    Manage billing
                  </button>
                )}
                <Link to="/farmers/websites"
                      className="rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase">
                  Open my shop <ArrowRight size={11} weight="bold" className="inline ml-1" />
                </Link>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <UsageTile label="Products" value={mine.usage.products}
                         limit={mine.plan.limits.products} />
              <UsageTile label="Inventory items" value={mine.usage.inventory_items} />
              <UsageTile label="Websites" value={mine.usage.websites} />
              {mine.subscription.current_period_end && (
                <UsageTile label="Renews" value={new Date(mine.subscription.current_period_end).toLocaleDateString()} />
              )}
            </div>
          </section>
        )}

        {/* Monthly / yearly toggle */}
        <div className="mt-8 flex items-center gap-2" data-testid="farmers-billing-interval-toggle">
          <button
            onClick={() => setInterval("month")}
            data-testid="farmers-billing-interval-month"
            className={`rounded-full border px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase transition ${
              interval === "month" ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]" : "bg-white text-[#3A3830] border-black/15"
            }`}
          >Monthly</button>
          <button
            onClick={() => setInterval("year")}
            data-testid="farmers-billing-interval-year"
            className={`rounded-full border px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase transition ${
              interval === "year" ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]" : "bg-white text-[#3A3830] border-black/15"
            }`}
          >Yearly · save 2 months</button>
        </div>

        {/* Plans grid */}
        <section className="mt-6 grid md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="farmers-billing-plans">
          {plans.map((p) => {
            const Icon = PLAN_ICON[p.id] || Leaf;
            const isMine = mine?.subscription.plan_id === p.id && mine?.subscription.status === "active";
            const priceLabel = p.id === "grower"
              ? (interval === "year" ? "$90/yr" : "$9/mo")
              : p.id === "business"
              ? (interval === "year" ? "$290/yr" : "$29/mo")
              : p.amount_display;
            return (
              <div key={p.id}
                   data-testid={`farmers-billing-plan-${p.id}`}
                   className={`rounded-3xl border p-6 bg-white flex flex-col ${
                     p.id === "grower" ? "border-[color:var(--np-brand,#B85F2A)] shadow-sm" : "border-black/10"
                   }`}>
                {p.id === "grower" && (
                  <div className="self-start rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.28em] uppercase mb-2">
                    Most popular
                  </div>
                )}
                <div className="flex items-center gap-2 text-[color:var(--np-brand,#B85F2A)]">
                  <Icon size={20} weight="fill" />
                  <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830]">{p.id}</div>
                </div>
                <div className="mt-1 font-serif-display text-2xl">{p.name}</div>
                <div className="text-[#3A3830] text-sm">{p.tagline}</div>
                <div className="mt-4 font-serif-display text-4xl">{priceLabel}</div>

                <ul className="mt-4 space-y-1.5 text-sm text-[#3A3830]">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex gap-2">
                      <Check size={14} weight="bold" className="text-[#0f7a3e] shrink-0 mt-0.5" /> {h}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => upgrade(p)}
                  data-testid={`farmers-billing-cta-${p.id}`}
                  disabled={busy === p.id || isMine}
                  className={`mt-6 rounded-full font-mono-tech text-[10px] tracking-[0.28em] uppercase px-4 py-2.5 transition ${
                    isMine ? "bg-neutral-100 text-[#3A3830] cursor-default"
                     : p.id === "grower" ? "bg-[color:var(--np-brand,#B85F2A)] text-white hover:opacity-90"
                     : "bg-[#1B4332] text-[#F5EBDC] hover:opacity-90"
                  } ${busy === p.id ? "opacity-60" : ""}`}
                >
                  {isMine ? "Your current plan"
                   : busy === p.id ? "Starting…"
                   : p.cta}
                </button>
              </div>
            );
          })}
        </section>

        {/* Feature matrix */}
        <FeatureMatrix plans={plans} />

        {/* FAQ */}
        <section className="mt-14" data-testid="farmers-billing-faq">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Common questions</div>
          <h2 className="mt-1 font-serif-display text-3xl">FAQ</h2>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            {FAQ.map(([q, a]) => (
              <div key={q} className="rounded-2xl border border-black/10 bg-white p-4">
                <div className="font-serif-display text-lg">{q}</div>
                <p className="mt-1 text-sm text-[#3A3830]">{a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      {coopOpen && (
        <CooperativeLeadModal onClose={() => setCoopOpen(false)} email={user?.email} />
      )}
    </main>
  );
}

function UsageTile({ label, value, limit }) {
  const pct = typeof value === "number" && typeof limit === "number" ? Math.min(100, (value / limit) * 100) : null;
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-3">
      <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</div>
      <div className="mt-1 font-serif-display text-xl">
        {value}{limit != null ? <span className="text-sm text-[#3A3830]"> / {limit}</span> : ""}
      </div>
      {pct != null && (
        <div className="mt-1 h-1 rounded-full bg-neutral-100 overflow-hidden">
          <div className="h-full bg-[color:var(--np-brand,#B85F2A)]" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

const FEATURE_KEYS = [
  ["shop_page",       "Farm shop page"],
  ["cart",            "Shopping cart"],
  ["deals",           "Deals & bulk offers"],
  ["surplus",         "Surplus & donation posts"],
  ["custom_domain",   "Custom domain"],
  ["watermark_remove", "Remove Farmers Pulse watermark"],
  ["farm_stay",       "Farm Stay bookings"],
  ["farm_dining",     "Farm Dining reservations"],
  ["experiences",     "Experiences (u-pick, tours)"],
  ["buyer_rfq",       "Buyer RFQs & wholesale"],
  ["invoicing",       "Invoicing & PDF exports"],
  ["pdf_reports",     "PDF harvest & inventory reports"],
  ["cooperative_admin","Multi-farm cooperative admin"],
  ["api_access",      "REST API access"],
];

function FeatureMatrix({ plans }) {
  if (!plans.length) return null;
  return (
    <section className="mt-14" data-testid="farmers-billing-matrix">
      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Compare plans</div>
      <h2 className="mt-1 font-serif-display text-3xl">Every feature, side by side</h2>
      <div className="mt-4 overflow-x-auto rounded-3xl border border-black/10 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="text-left px-4 py-3 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Feature</th>
              {plans.map((p) => (
                <th key={p.id} className="text-left px-4 py-3 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
                  {p.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FEATURE_KEYS.map(([k, label]) => (
              <tr key={k} className="border-t border-black/5">
                <td className="px-4 py-2 text-[#3A3830]">{label}</td>
                {plans.map((p) => (
                  <td key={p.id} className="px-4 py-2">
                    {p.features[k]
                      ? <Check size={16} weight="bold" className="text-[#0f7a3e]" />
                      : <X size={14} weight="bold" className="text-neutral-300" />}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-black/5">
              <td className="px-4 py-2 text-[#3A3830]">Product limit</td>
              {plans.map((p) => (
                <td key={p.id} className="px-4 py-2 text-[#3A3830]">
                  {p.limits.products == null ? "Unlimited" : p.limits.products}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

const FAQ = [
  ["Does Farmers Pulse take a cut of my sales?",
   "No commissions on your farm shop sales — you keep 100% (minus your payment processor's fee). The subscription is all we charge."],
  ["Can I switch plans later?",
   "Yes, any time. Upgrade instantly and we prorate; downgrade at the end of your billing period."],
  ["What happens on Free after 10 products?",
   "You can still edit, un-publish and rotate products. To publish more than 10 at once, upgrade to Grower for unlimited."],
  ["Do you support Indian farmers?",
   "Yes — Razorpay UPI checkout is coming next, and you can already accept UPI QR codes on your shop page under Business."],
  ["Can I bring my own domain?",
   "Grower and above — point a domain like mango.farm to Farmers Pulse and we handle TLS automatically."],
  ["What about my school garden or research farm?",
   "Ping the Cooperative plan — we set you up with a multi-farm account and API access, priced per your programme."],
];

function CooperativeLeadModal({ onClose, email }) {
  const [form, setForm] = useState({
    farm_count: 5, contact_name: "", contact_email: email || "",
    country: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await http.post("/billing/cooperative/lead", form);
      toast.success("Thanks — we'll be in touch within 2 business days.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-[1500] flex items-center justify-center px-4"
         data-testid="farmers-coop-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Cooperative</div>
            <h3 className="mt-1 font-serif-display text-2xl">Talk to us about your collective</h3>
          </div>
          <button onClick={onClose} data-testid="farmers-coop-close" className="text-neutral-500 hover:text-black">
            <X size={20} />
          </button>
        </div>
        <div className="mt-4 space-y-3">
          <Field label="How many farms?">
            <input type="number" min={2} className="w-full rounded-xl border border-black/15 px-3 py-2"
                   value={form.farm_count} onChange={(e) => setForm({ ...form, farm_count: +e.target.value })} />
          </Field>
          <Field label="Contact name">
            <input className="w-full rounded-xl border border-black/15 px-3 py-2"
                   value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
          </Field>
          <Field label="Contact email">
            <input type="email" className="w-full rounded-xl border border-black/15 px-3 py-2"
                   value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          </Field>
          <Field label="Country">
            <input className="w-full rounded-xl border border-black/15 px-3 py-2"
                   value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
          </Field>
          <Field label="Notes (optional)">
            <textarea rows={3} className="w-full rounded-xl border border-black/15 px-3 py-2"
                      value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <button
            disabled={busy}
            onClick={submit}
            data-testid="farmers-coop-submit"
            className="mt-2 w-full rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send request"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</div>
      <div className="mt-1">{children}</div>
    </label>
  );
}
